import { supabase } from '../lib/supabase';

// Helper function to derive phase status from progress
const derivePhaseStatus = (progress) => {
  if (progress === 0) return 'pending';
  if (progress > 0 && progress < 100) return 'ongoing';
  if (progress === 100) return 'completed';
  return 'pending'; // fallback
};

export const phaseService = {
  // Get all phases for a project
  async getProjectPhases(projectId) {
    const { data, error } = await supabase?.from('phases')?.select(`
        *,
        assigned_worker:users!phases_assigned_worker_id_fkey(
          id,
          full_name,
          email,
          trade,
          avatar_url
        )
      `)?.eq('project_id', projectId)?.order('phase_order', { ascending: true });

    if (error) {
      console.error('Error fetching project phases:', error);
      throw new Error(error?.message || 'Failed to fetch project phases');
    }

    // Use the actual status from the database; only fall back to derived status if status is null/undefined
    const phasesWithStatus = data?.map(phase => ({
      ...phase,
      status: phase?.status || derivePhaseStatus(phase?.progress || 0)
    }));

    return phasesWithStatus;
  },

  // Get single phase details
  async getPhaseById(phaseId) {
    const { data, error } = await supabase?.from('phases')?.select(`
        *,
        assigned_worker:users!phases_assigned_worker_id_fkey(
          id,
          full_name,
          email,
          trade,
          avatar_url
        ),
        remarks:phase_remarks(
          id,
          remark_text,
          label,
          created_at,
          created_by:users!phase_remarks_created_by_fkey(
            id,
            full_name,
            email
          )
        )
      `)?.eq('id', phaseId)?.single();

    if (error) {
      console.error('Error fetching phase details:', error);
      throw new Error(error?.message || 'Failed to fetch phase details');
    }

    // Use the actual status from the database; only fall back to derived status if status is null/undefined
    const phaseWithStatus = {
      ...data,
      status: data?.status || derivePhaseStatus(data?.progress || 0)
    };

    return phaseWithStatus;
  },

  // Update phase progress
  async updatePhaseProgress(phaseId, progress) {
    const updateData = { 
      progress: progress,
      status: derivePhaseStatus(progress),
      updated_at: new Date()?.toISOString()
    };

    // If progress is 100, set actual_end_date
    if (progress === 100) {
      updateData.actual_end_date = new Date()?.toISOString()?.split('T')?.[0];
    }

    // If progress > 0, set actual_start_date if not already set
    if (progress > 0) {
      const { data: currentPhase } = await supabase?.from('phases')?.select('actual_start_date')?.eq('id', phaseId)?.single();
      if (!currentPhase?.actual_start_date) {
        updateData.actual_start_date = new Date()?.toISOString()?.split('T')?.[0];
      }
    }

    const { data, error } = await supabase?.from('phases')?.update(updateData)?.eq('id', phaseId)?.select()?.single();

    if (error) {
      console.error('Error updating phase progress:', error);
      throw new Error(error?.message || 'Failed to update phase progress');
    }

    return data;
  },

  // Add phase remark
  async addPhaseRemark(phaseId, projectId, remarkText, label) {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase?.from('phase_remarks')?.insert({
        phase_id: phaseId,
        project_id: projectId,
        remark_text: remarkText,
        label: label,
        created_by: user?.id,
        created_at: new Date()?.toISOString()
      })?.select(`
        *,
        created_by:users!phase_remarks_created_by_fkey(
          id,
          full_name,
          email
        )
      `)?.single();

    if (error) {
      console.error('Error adding phase remark:', error);
      throw new Error(error?.message || 'Failed to add phase remark');
    }

    return data;
  },

  // Get phase remarks
  async getPhaseRemarks(phaseId) {
    const { data, error } = await supabase?.from('phase_remarks')?.select(`
        *,
        created_by:users!phase_remarks_created_by_fkey(
          id,
          full_name,
          email
        )
      `)?.eq('phase_id', phaseId)?.order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching phase remarks:', error);
      throw new Error(error?.message || 'Failed to fetch phase remarks');
    }

    return data;
  },

  // Update project current phase
  async updateProjectCurrentPhase(projectId, phaseName) {
    const { data, error } = await supabase?.from('projects')?.update({ 
        current_phase: phaseName,
        updated_at: new Date()?.toISOString()
      })?.eq('id', projectId)?.select()?.single();

    if (error) {
      console.error('Error updating project current phase:', error);
      throw new Error(error?.message || 'Failed to update project current phase');
    }

    return data;
  },

  // Upload phase photo
  async uploadPhasePhoto(phaseId, projectId, file) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Generate unique file name
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${Date.now()}_${Math.random()?.toString(36)?.substring(7)}.${fileExt}`;
      const filePath = `${phaseId}/${fileName}`;

      // Upload to storage
      const { data: uploadData, error: uploadError } = await supabase?.storage
        ?.from('phase-photos')
        ?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Record in database
      const { data: photoRecord, error: dbError } = await supabase
        ?.from('phase_photos')
        ?.insert({
          phase_id: phaseId,
          project_id: projectId,
          uploaded_by: user?.id,
          file_path: uploadData?.path,
          file_name: file?.name,
          file_size: file?.size
        })
        ?.select()
        ?.single();

      if (dbError) throw dbError;

      return photoRecord;
    } catch (error) {
      console.error('Error uploading phase photo:', error);
      throw new Error(error?.message || 'Failed to upload photo');
    }
  },

  // Get phase photos
  async getPhasePhotos(phaseId) {
    try {
      const { data, error } = await supabase
        ?.from('phase_photos')
        ?.select(`
          *,
          uploaded_by:users!phase_photos_uploaded_by_fkey(
            id,
            full_name
          )
        `)
        ?.eq('phase_id', phaseId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      // Get signed URLs for each photo
      const photosWithUrls = await Promise.all(
        data?.map(async (photo) => {
          const { data: urlData } = await supabase?.storage
            ?.from('phase-photos')
            ?.createSignedUrl(photo?.file_path, 3600); // 1 hour expiry

          return {
            ...photo,
            url: urlData?.signedUrl
          };
        })
      );

      return photosWithUrls;
    } catch (error) {
      console.error('Error fetching phase photos:', error);
      throw new Error(error?.message || 'Failed to fetch phase photos');
    }
  },

  // Delete phase photo
  async deletePhasePhoto(photoId, filePath) {
    try {
      // Delete from storage
      const { error: storageError } = await supabase?.storage
        ?.from('phase-photos')
        ?.remove([filePath]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        ?.from('phase_photos')
        ?.delete()
        ?.eq('id', photoId);

      if (dbError) throw dbError;

      return true;
    } catch (error) {
      console.error('Error deleting phase photo:', error);
      throw new Error(error?.message || 'Failed to delete photo');
    }
  },

  // Update 3M trade progress (for 3 Musketeers phases)
  async update3MTradeProgress(phaseId, trade, progress) {
    const tradeColumn = `${trade}_progress`;
    const updateData = {
      [tradeColumn]: progress,
      updated_at: new Date()?.toISOString()
    };

    const { data, error } = await supabase
      ?.from('phases')
      ?.update(updateData)
      ?.eq('id', phaseId)
      ?.select()
      ?.single();

    if (error) {
      console.error(`Error updating ${trade} progress:`, error);
      throw new Error(error?.message || `Failed to update ${trade} progress`);
    }

    // Calculate average and update overall progress
    const avgProgress = Math.round(
      (data?.plumber_progress + data?.electrician_progress + data?.aircon_progress) / 3
    );

    // Update overall phase progress
    await this.updatePhaseProgress(phaseId, avgProgress);

    // If average reaches 90%, notify next phase workers
    if (avgProgress >= 90) {
      await this.notifyNextPhaseWorkers(data?.project_id, phaseId);
    }

    return data;
  },

  // Get 3M progress for a phase
  async get3MProgress(phaseId) {
    const { data, error } = await supabase
      ?.from('phases')
      ?.select('plumber_progress, electrician_progress, aircon_progress')
      ?.eq('id', phaseId)
      ?.single();

    if (error) {
      console.error('Error fetching 3M progress:', error);
      throw new Error(error?.message || 'Failed to fetch 3M progress');
    }

    return {
      plumber: data?.plumber_progress || 0,
      electrician: data?.electrician_progress || 0,
      aircon: data?.aircon_progress || 0,
      average: Math.round(
        (data?.plumber_progress + data?.electrician_progress + data?.aircon_progress) / 3
      )
    };
  },

  // Notify next phase workers when 3M average reaches 90%
  async notifyNextPhaseWorkers(projectId, currentPhaseId) {
    try {
      // Get current phase order
      const { data: currentPhase } = await supabase
        ?.from('phases')
        ?.select('phase_order')
        ?.eq('id', currentPhaseId)
        ?.single();

      if (!currentPhase) return;

      // Get next phase
      const { data: nextPhase } = await supabase
        ?.from('phases')
        ?.select('id, phase_name, required_trade')
        ?.eq('project_id', projectId)
        ?.eq('phase_order', currentPhase?.phase_order + 1)
        ?.single();

      if (!nextPhase) return;

      // Get workers assigned to next phase via phases.assigned_worker_id
      const { data: nextPhaseData } = await supabase
        ?.from('phases')
        ?.select('assigned_worker_id')
        ?.eq('id', nextPhase?.id)
        ?.single();

      const workerIds = nextPhaseData?.assigned_worker_id
        ? [{ worker_id: nextPhaseData?.assigned_worker_id }]
        : [];

      if (!workerIds || workerIds?.length === 0) return;

      // Create notifications for each worker
      const notifications = workerIds?.map(assignment => ({
        user_id: assignment?.worker_id,
        title: 'Your phase is approaching',
        message: `The 3 Musketeers phase has reached 90% completion. Your phase "${nextPhase?.phase_name}" will begin soon.`,
        type: 'phase_update',
        related_project_id: projectId,
        related_phase_id: nextPhase?.id
      }));

      await supabase?.from('notifications')?.insert(notifications);
    } catch (error) {
      console.error('Error notifying next phase workers:', error);
      // Don't throw - this is a non-critical operation
    }
  },

  // Assign a worker to a phase by contact info
  async updatePhaseWorker(phaseId, contact, method) {
    try {
      let workerQuery = supabase?.from('users')?.select('id, full_name, email, phone, trade, avatar_url');

      if (method === 'email') {
        workerQuery = workerQuery?.eq('email', contact?.trim());
      } else if (method === 'phone') {
        workerQuery = workerQuery?.eq('phone', contact?.trim());
      } else {
        // username — partial match on full_name
        workerQuery = workerQuery?.ilike('full_name', `%${contact?.trim()}%`);
      }

      const { data: matchedWorkers, error: lookupError } = await workerQuery?.limit(5);
      if (lookupError) throw lookupError;
      if (!matchedWorkers || matchedWorkers?.length === 0) {
        throw new Error(`No worker found matching "${contact}"`);
      }

      const worker = matchedWorkers?.[0];

      const { error: updateError } = await supabase
        ?.from('phases')
        ?.update({ assigned_worker_id: worker?.id })
        ?.eq('id', phaseId);

      if (updateError) throw updateError;

      return worker;
    } catch (error) {
      console.error('Error updating phase worker:', error);
      throw error;
    }
  },

  // Remove worker assignment from a phase
  async removePhaseWorker(phaseId) {
    const { error } = await supabase
      ?.from('phases')
      ?.update({ assigned_worker_id: null })
      ?.eq('id', phaseId);
    if (error) throw error;
  },

  // Helper: Get role-to-phase mapping
  getRolePhaseMapping() {
    return {
      'hacker': ['Hacking'],
      'plumber': ['3 Musketeers', '3 Musketeers Installation'],
      'electrician': ['3 Musketeers', '3 Musketeers Installation'],
      'aircon': ['3 Musketeers', '3 Musketeers Installation'],
      'false_ceiling': ['False Ceiling'],
      'tiling': ['Tiling'],
      'carpenter': ['Carpentry Measurement', 'Carpentry Installation'],
      'cleaner': ['Clearing and Cleaning'],
      'painter': ['Painting', 'Paint Touch Up'],
      'main_contractor': ['Handover']
    };
  }
};